import type { PracticeQuizAttemptFragment, UserBookProgressFragment } from "./generated";
import { api } from "./generated";

/**
 * AIDEV-NOTE: Mutace vracejí celý nový stav dokumentu. Místo invalidace tagů (druhý
 * request po každém otočení kartičky) se odpověď zapíše rovnou do cache — všechny
 * instance hooků (kartičky, kvíz, ExamIntro) tak čtou tentýž čerstvý stav. Hooky
 * featury se importují odsud, ne z api/generated, aby enhancement vždy proběhl před
 * prvním použitím. [TD-315]
 *
 * AIDEV-NOTE: Od TD-353 míří patch otočení do `getUserBookProgress`, ne do zaniklého
 * `practiceSession` — `flips` bydlí ve stejném dokumentu jako značky. Mutace proto
 * musí vybírat CELÝ fragment `userBookProgress`; kdyby vynechala pole, tenhle patch
 * by ho z cache utrhl. [TD-353]
 */
const upsertProgress = (bookId: string, progress: UserBookProgressFragment) =>
  practiceQuizAttemptApi.util.updateQueryData("getUserBookProgress", { bookId }, draft => {
    draft.userBookProgress = progress;
  });

/**
 * AIDEV-NOTE: Pokus kvízu je od TD-352 samostatný dokument. Do TD-353 jezdil jako druhé
 * kořenové pole vedle session; po přesunu `flips` do `getUserBookProgress` zbyl
 * v tom dotazu sám — proto se od TD-357 jmenuje getPracticeQuizAttempt. [TD-352, TD-353]
 */
const upsertAttempt = (bookId: string, attempt: PracticeQuizAttemptFragment) =>
  practiceQuizAttemptApi.util.updateQueryData("getPracticeQuizAttempt", { bookId }, draft => {
    draft.practiceQuizAttempt = attempt;
  });

export const practiceQuizAttemptApi = api.enhanceEndpoints({
  endpoints: {
    flipPracticeCard: {
      async onQueryStarted({ bookId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertProgress(bookId, data.practiceCardFlip));
        } catch {
          // Chybu hlásí error stav mutace u volajícího — cache se nechává být.
        }
      },
    },
    startQuizAttempt: {
      async onQueryStarted({ bookId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertAttempt(bookId, data.practiceQuizAttemptStart));
        } catch {
          // Chybu hlásí error stav mutace u volajícího — cache se nechává být.
        }
      },
    },
    rateQuizQuestion: {
      async onQueryStarted({ bookId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(upsertAttempt(bookId, data.practiceQuizQuestionRate));
        } catch {
          // Chybu hlásí error stav mutace u volajícího — cache se nechává být.
        }
      },
    },
  },
});

export const {
  useGetPracticeQuizAttemptQuery,
  useFlipPracticeCardMutation,
  useStartQuizAttemptMutation,
  useRateQuizQuestionMutation,
} = practiceQuizAttemptApi;
